import { defineComponent, h, onBeforeUnmount, onMounted, ref, unref } from 'vue';

import { classesStates, keyNames, partition, pickAttrs } from '@components/utils';

import { makeDisabledProps, makeReadonlyProps, makeTagProps, useRender } from '../../../composable';
import { makeUploadProps, useRequest } from '../composable/';
import { attrAccept, getUid, traverseFileTree } from '../utils';

import type { NUploader } from '../interface';
import type { PropType } from 'vue';

export const componentName = 'CdsAjaxUploader';
export default defineComponent({
  name: componentName,
  inheritAttrs: false,
  props: {
    id: {
      type: String as PropType<string>,
      default: undefined
    },

    ...makeUploadProps({
      data: {},
      headers: {},
      name: 'file',
      multiple: false,
      customRequest: null,
      withCredentials: false,
      beforeUpload: null,
      openFileDialogOnClick: true
    }),
    ...makeDisabledProps(),
    ...makeReadonlyProps(),
    ...makeTagProps({
      tag: 'span'
    })
  },
  emits: {
    mouseenter: (event: MouseEvent) => true,
    mouseleave: (event: MouseEvent) => true,
    click: (event: MouseEvent | KeyboardEvent) => true,
    batchStart: (
      fileList: {
        file: NUploader.IRcFile | null;
        parsedFile: Exclude<NUploader.TBeforeUploadFileType, boolean> | null;
      }[]
    ) => true,
    reject: (fileList: NUploader.IRcFile[]) => true,
    start: (file: NUploader.IRcFile) => true,
    error: (
      error: NUploader.IUploadRequestError | ProgressEvent,
      response: Record<string, unknown>,
      file: NUploader.IRcFile | null
    ) => true,
    success: (response: Record<string, unknown>, file: NUploader.IRcFile | null, xhr?: XMLHttpRequest) => true,
    progress: (event: NUploader.IUploadProgressEvent, file: NUploader.IRcFile | null) => true
  },
  setup(props, { slots, attrs, emit }) {
    const uid = ref(getUid());
    const defaultRequest = useRequest();

    const reqs: any = {};
    let isMounted = false;

    const fileInput = ref<HTMLInputElement>();

    async function processFile(file: NUploader.IRcFile, fileList: NUploader.IRcFile[]): Promise<NUploader.IParsedFile> {
      const { beforeUpload } = props;

      let transformedFile: NUploader.TBeforeUploadFileType | void = file;
      if (beforeUpload) {
        try {
          transformedFile = await beforeUpload(file, fileList);
        } catch (e) {
          transformedFile = false;
        }

        if (transformedFile === false) {
          return {
            origin: file,
            parsedFile: null,
            action: null,
            data: null
          };
        }
      }

      // Get latest action
      const { action } = props;

      let mergedAction: string | undefined;

      if (typeof action === 'function') {
        mergedAction = await action(file);
      } else {
        mergedAction = action;
      }

      // Get latest data
      const { data } = props;

      let mergedData: Record<string, unknown> | undefined;

      if (typeof data === 'function') {
        mergedData = await data(file);
      } else {
        mergedData = data;
      }

      const parsedData =
        (typeof transformedFile === 'object' || typeof transformedFile === 'string') && transformedFile
          ? transformedFile
          : file;

      let parsedFile: File;

      if (parsedData instanceof File) {
        parsedFile = parsedData;
      } else {
        parsedFile = new File([parsedData], file.name, { type: file.type });
      }

      const mergedParsedData: NUploader.IRcFile = parsedFile as NUploader.IRcFile;
      mergedParsedData.uid = file.uid;

      return {
        origin: file,
        data: mergedData,
        parsedFile: mergedParsedData,
        action: mergedAction
      };
    }

    function post({ data, origin, action, parsedFile }: NUploader.IParsedFile) {
      if (!isMounted) {
        return;
      }

      const { name, headers, withCredentials, method, customRequest, onStart } = props;

      const { uid } = origin;
      const request = customRequest || defaultRequest;

      const requestOptions: NUploader.IUploadRequestOption = {
        action,
        filename: name,
        data,
        file: parsedFile,
        headers,
        withCredentials,
        method: method || 'post',
        onProgress: (event: NUploader.IUploadProgressEvent) => {
          emit('progress', event, parsedFile);
        },
        onSuccess: (response: any, xhr?: XMLHttpRequest) => {
          emit('success', response, parsedFile, xhr);

          delete reqs[uid];
        },
        onError: (error: NUploader.IUploadRequestError | ProgressEvent, response: any) => {
          emit('error', error, response, parsedFile);

          delete reqs[uid];
        }
      };

      emit('start', origin);
      reqs[uid] = request(requestOptions);
    }

    function abort(file?: any) {
      if (file) {
        const uid = file.uid ? file.uid : file;

        if (reqs[uid] && reqs[uid].abort) {
          reqs[uid].abort();
        }
        delete reqs[uid];
      } else {
        Object.keys(reqs).forEach((uid) => {
          if (reqs[uid] && reqs[uid].abort) {
            reqs[uid].abort();
          }
          delete reqs[uid];
        });
      }
    }

    function reset() {
      uid.value = getUid();
    }

    onMounted(() => {
      isMounted = true;
    });

    onBeforeUnmount(() => {
      isMounted = false;
      abort();
    });

    function uploadFiles(files: File[]) {
      const originFiles = [...files] as NUploader.IRcFile[];
      const postFiles = originFiles.map((file: NUploader.IRcFile & { uid?: string }) => {
        file.uid = String(getUid());
        return processFile(file, originFiles);
      });

      Promise.all(postFiles).then((fileList) => {
        emit(
          'batchStart',
          fileList.map(({ origin, parsedFile }) => ({ file: origin, parsedFile }))
        );

        fileList
          .filter((file) => file.parsedFile !== null)
          .forEach((file) => {
            post(file);
          });
      });
    }

    function onChange(event: Event) {
      const { accept, directory } = props;
      const { files } = event.target as HTMLInputElement & { files: NUploader.IRcFile[] };
      const acceptedFies = [...files].filter((file: NUploader.IRcFile) => !directory || attrAccept(file, accept));

      uploadFiles(acceptedFies);
      reset();
    }

    function onClick(event: MouseEvent | KeyboardEvent) {
      const el = unref(fileInput);

      if (!el) return;

      const { onClick } = props;

      el.click();

      if (onClick) {
        onClick(event);
      }
    }

    function onKeydown(event: KeyboardEvent) {
      if (event.key === keyNames.enter) {
        onClick(event);
      }
    }

    function onFileDrop(event: DragEvent) {
      const { multiple, directory, onReject } = props;

      event.preventDefault();

      if (event.type === 'dragover') return;

      if (directory) {
        traverseFileTree(
          Array.prototype.slice.call(event.dataTransfer?.items),
          uploadFiles,
          (_file: NUploader.IRcFile) => attrAccept(_file, props.accept)
        );
      } else {
        const files: [NUploader.IRcFile[], NUploader.IRcFile[]] = partition<NUploader.IRcFile>(
          Array.prototype.slice.call(event.dataTransfer?.files),
          (file: NUploader.IRcFile) => attrAccept(file, props.accept)
        );

        let successFiles = files[0];
        const errorFiles = files[1];

        if (multiple === false) {
          successFiles = successFiles.slice(0, 1);
        }

        uploadFiles(successFiles);
        if (errorFiles.length && onReject) onReject(errorFiles);
      }
    }

    useRender(() => {
      const {
        tag,
        disabled,
        readonly,
        id,
        multiple,
        accept,
        capture,
        directory,
        openFileDialogOnClick,
        onMouseenter,
        onMouseleave,
        ...spreadProps
      } = props;

      const events =
        readonly || disabled
          ? {}
          : {
              onClick: props.openFileDialogOnClick ? onClick : () => {},
              onKeydown: props.openFileDialogOnClick ? onKeydown : () => {},
              onMouseenter: props.onMouseenter,
              onMouseleave: props.onMouseleave,
              onDrop: onFileDrop,
              onDragover: onFileDrop
            };

      const dirProps = directory ? { directory: 'directory', webkitdirectory: 'webkitdirectory' } : {};

      return h(
        props.tag,
        {
          role: 'button',
          style: attrs.style,
          class: {
            [classesStates.disabled]: disabled,
            [classesStates.readonly]: readonly,
            [attrs.class as string]: !!attrs.class
          },
          ...events
        },
        [
          h('input', {
            ...pickAttrs(spreadProps, { aria: true, data: true }),
            id,
            ref: fileInput,
            type: 'file',
            key: unref(uid),
            accept,
            multiple,
            disabled,
            readonly,
            style: { display: 'none' },
            onChange,
            onClick: (event: Event) => event.stopPropagation(),
            ...dirProps,
            ...(props.capture != null ? { capture: props.capture } : {})
          }),
          slots.default?.()
        ]
      );
    });

    return {
      fileInput,
      abort
    };
  }
});
